import { Info, X } from 'lucide-react';
import { useState } from 'react';
import { TenantSelector } from '../../../../components/tenant-selector/TenantSelector';
import type { TenantSelectorRowData } from '../../../../components/tenant-selector/types';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import type { TenantSelection } from '../../automations-tab/templates.types';
import { useAutomationEditorContext } from '../AutomationEditorContext';
import { useEditorSyncStatus } from '../EditorSyncStatusContext';
import { useTenantSegmentConflicts } from '../useTenantSegmentConflicts';
import styles from './TenantSelectionControl.module.css';
import { TenantSelectionHelpPanel } from './TenantSelectionHelpPanel';
import { TenantSelectionValidationStatus } from './TenantSelectionValidationStatus';

interface TenantSelectionDialogProps {
  templateId: string;
  onClose: () => void;
  tenantRowData: Record<string, TenantSelectorRowData>;
  isLoading?: boolean;
}

export function TenantSelectionDialog({
  templateId,
  onClose,
  tenantRowData,
  isLoading,
}: TenantSelectionDialogProps) {
  const { editor } = useAutomationEditorContext();
  const { state, updateTenantSelection } = editor;
  const disabled = useEditorSyncStatus();
  const { tenantSelection } = state;

  // Snapshotted on mount; the parent conditionally renders this dialog, so each open starts fresh.
  const [localSelection, setLocalSelection] = useState<TenantSelection>(tenantSelection);
  const [helpOpen, setHelpOpen] = useState(false);
  const { conflicts, isValidating } = useTenantSegmentConflicts(templateId, localSelection);

  const handleConfirm = () => {
    updateTenantSelection(localSelection);
    onClose();
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header>
          <hgroup>
            <h2>Tenant Selection</h2>
            <p>Choose which tenants receive this automation when you save.</p>
          </hgroup>
          <button
            type="button"
            onClick={() => setHelpOpen(open => !open)}
            className={styles.helpButton}
            aria-expanded={helpOpen}>
            <Info aria-hidden="true" />
            {helpOpen ? 'Hide explanation' : 'Learn more'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close tenant selection">
            <X />
          </button>
        </header>

        <main>
          {helpOpen && <TenantSelectionHelpPanel context="automation" />}
          <TenantSelector
            selection={localSelection}
            onChange={setLocalSelection}
            includeDraftOption
            draftSelectionDescription="Not synced to anyone yet. Keep building — nobody receives it until you sync it to tenants."
            allTenantsSelectionDescription="New tenants will automatically receive this automation when they're added."
            selectedTenantsSelectionDescription="Only the tenants you select below will receive this automation. New tenants will not receive it unless you add them."
            disabled={disabled}
            tenantRowData={tenantRowData}
            isLoading={isLoading}
          />

          {conflicts && (
            <TenantSelectionValidationStatus conflicts={conflicts} isValidating={isValidating} />
          )}
        </main>

        <footer className={styles.dialogFooter}>
          <button type="button" onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} className={styles.confirmButton}>
            OK
          </button>
        </footer>
      </dialog>
    </>
  );
}
